// Imports and global variable declarations
import {trans} from "../i18n/i18n";
import {MESSAGE_TYPES} from "../consts/serverEvents";
import {showToast} from "./toast";
import {initLookupComponent} from "./lookup";
import {loadAndShowModal} from "./modal";
import {bindModalTriggerLinks, initializeLoginModal} from "./authentication";
import * as decodeConsts from "../enums/decode";
import * as loadingStates from "../enums/loadingStates";
import {route} from "ziggy-js";
import {Toast} from "../consts/toast";
import {requestVehicleReport} from "../api/vehicleReportOrder";

let stream, vin;
let isAlreadyPurchased = false;
let retryCount = 0;
let hasCredits = true;
let hasUser = true;
let latestData = {};

const MAX_RETRIES = 5;
const TIMEOUT_LIMIT_MS = 10000;
const STEP_TIMEOUTS = new Map();

// Configuration: maps each step to its data group
const STEP_CONFIG = {
  "decode-step": decodeConsts.GROUP.DECODE,
  "market-value-dynamic-data-step": decodeConsts.GROUP.CHART,
  "price-distribution-data-step": decodeConsts.GROUP.CHART,
  "price-odo-data-step": decodeConsts.GROUP.CHART,
  "stolen-check-data-step": decodeConsts.GROUP.POLICE,
  "vehicle-wreckers-data-step": decodeConsts.GROUP.WRECKAGE
};

const VIN_DECODE_STEPS = Object.keys(STEP_CONFIG);

// Initialize functionalities once the DOM is fully loaded
document.addEventListener('DOMContentLoaded', initDecodePaidPage);
document.addEventListener('DOMContentLoaded', initDecodePaidModalPage);

/**
 * Initializes the modal and sets up the event stream for the VIN lookup.
 */
async function initDecodePaidPage() {
    const vinDecodepageElement = document.getElementById('vin-decode-paid-page');
    if (!vinDecodepageElement) {
        return;
    }
    const fullReportData = JSON.parse(vinDecodepageElement.getAttribute("data-full-report-data"));
    initLookupComponent(fullReportData);
}
/**
 * Initializes the modal and sets up the event stream for the VIN lookup.
 */
async function initDecodePaidModalPage() {
    const vinDecodeElement = document.getElementById('vin-decode-paid');
    if (!vinDecodeElement) {
        return;
    }
    isAlreadyPurchased = vinDecodeElement.getAttribute('data-already-purchased') === '1';
    await loadAndShowModal('decode-modal');
    vin = document.querySelector('[data-vin]').getAttribute('data-vin');
    setupEventStream(vin);
}
/**
 * Sets up the EventSource stream to listen for updates on the VIN lookup process.
 * @param {string} vin - The VIN number being looked up.
 */
function setupEventStream(vin) {
    stream = new EventSource(
      route("jsapi.stream.vehicleReportOverview", {vin: vin}),
      {withCredentials: true}
    );

    initStepTimeouts();

    stream.onerror = (e) => {
        console.error('EventSource Error:', e);
        retryCount++;
        console.log(`Retry attempt: ${retryCount}`);
        if (retryCount > MAX_RETRIES) {
            console.error('Maximum number of retry attempts exceeded.');
            stream.close();
        }
        console.error(`A connection error occurred at ${new Date().toISOString()}`);
    };
    stream.onmessage = handleStreamMessage;
}
/**
 * Handles messages from the server during the VIN lookup process.
 * @param {MessageEvent} event - The event containing the message from the server.
 */
function handleStreamMessage(event) {
  try {
    const {message, type} = JSON.parse(event.data);

    switch (type) {
      case MESSAGE_TYPES.DECODE_IN_PROGRESS:
        updateProgress(message);
        break;
      case MESSAGE_TYPES.DECODE_COMPLETED:
        clearAllStepTimeouts();
        updateProgress(message, true);
        stream.close();
        break;
      case MESSAGE_TYPES.INVALID_REQUEST:
        finalizeVINLookupDecode(false);
        stream.close();
        break;
      case MESSAGE_TYPES.HEARTBEAT:
        // Noop
        break;
      case MESSAGE_TYPES.NO_CREDITS:
        hasCredits = false;
        break;
      case MESSAGE_TYPES.NO_USER:
        hasUser = false;
        break;
      default:
        console.error("Unknown message type:", type);
    }

  } catch (e) {
    console.error('Error parsing data:', e);
  }
}

/**
 * Updates the progress indicators for each step of the VIN decoding process.
 * This function is called during both the intermediate progress updates and the final completion update.
 * @param {Object} data - The data object containing the status of each decoding step.
 * @param {boolean} [isFinal=false] - A flag indicating whether the update is a final update. This is used to determine if additional finalization actions should be taken.
 */
function updateProgress(data, isFinal = false) {
    // Store latest data for timeout handling
    latestData = data;
    
    const policeStatus = data[decodeConsts.GROUP.POLICE] || {};
    const wreckageStatus = data[decodeConsts.GROUP.WRECKAGE] || {};

    // Count successful police checks
    const policeSuccessCount = Object.values(policeStatus)
        .filter(status => status === loadingStates.LOADING_STATES.SUCCESS)
        .length;

    // Check if all police checks are complete
    let stolenStatuses = Object.values(policeStatus);
    let stolenStatus;

    const { FAILED, LOADING, SUCCESS } = loadingStates.LOADING_STATES;

    // 1) Ensure stolenStatuses is an array
    if (!Array.isArray(stolenStatuses)) {
        console.error('Expected stolenStatuses to be an array, got:', stolenStatuses);
        stolenStatuses = [];
    }

    // 2) Detect any unexpected status values
    const validStates = [FAILED, LOADING, SUCCESS];
    const invalidStates = stolenStatuses.filter(s => !validStates.includes(s));
    if (invalidStates.length) {
        console.error('Invalid status values detected in stolenStatuses:', invalidStates);
    }

    // 3) Determine aggregate status
    if (stolenStatuses.length === 0 || stolenStatuses.every(s => s === FAILED)) {
        // no items or all attempts have failed → mark as FAILED
        stolenStatus = FAILED;
    } else if (stolenStatuses.includes(LOADING)) {
        // at least one attempt is still in progress → mark as LOADING
        stolenStatus = LOADING;
    } else {
        // otherwise (no loading, not all failed) → at least one succeeded → mark as SUCCESS
        stolenStatus = SUCCESS;
    }

    updateStolenCheckText(policeSuccessCount);

    // Count successful wreckage checks
    const wreckageSuccessCount = Object.values(wreckageStatus)
        .filter(status => status === loadingStates.LOADING_STATES.SUCCESS)
        .length;

    // Check if all wreckage checks are complete
    const isWreckageCheckComplete = Object.values(wreckageStatus)
        .every(status => status === loadingStates.LOADING_STATES.SUCCESS);

    updateWreckageCheckText(wreckageSuccessCount);
    updateStepStatus("decode-step", data[decodeConsts.GROUP.DECODE][decodeConsts.DECODE_GLOBAL]);
    updateStepStatus("market-value-dynamic-data-step", data[decodeConsts.GROUP.CHART][decodeConsts.AVG_PRICE_AND_ODO_CHART]);
    updateStepStatus("price-distribution-data-step", data[decodeConsts.GROUP.CHART][decodeConsts.DEPRECATION_CHART]);
    updateStepStatus("price-odo-data-step", data[decodeConsts.GROUP.CHART][decodeConsts.PRICE_VS_ODO_CHART]);
    updateStepStatus("stolen-check-data-step", stolenStatus);
    updateStepStatus("vehicle-wreckers-data-step", isWreckageCheckComplete ? loadingStates.LOADING_STATES.SUCCESS : loadingStates.LOADING_STATES.LOADING);

    if (isFinal) {
        // Check if any step was successful
        const isSuccess = VIN_DECODE_STEPS.some(stepId => {
            const stepElement = document.getElementById(stepId);
            return stepElement && stepElement.classList.contains('success-step');
        });
        finalizeVINLookupDecode(isSuccess);
        return;
    }
}

/**
 * Updates the status of a specific step in the lookup process.
 * @param {string} stepId - The ID of the step element to be updated.
 * @param {boolean} status - Indicates the status of the step.
 */
function updateStepStatus(stepId, status) {
    const stepElement = document.getElementById(stepId);
    if (!stepElement) return;
    const icon = stepElement.querySelector(".close-icon");

    switch (status) {
        case loadingStates.LOADING_STATES.SUCCESS:
            stepElement.classList.remove("loading-step", "error-step");
            stepElement.classList.add("success-step");
            if (icon) {
                icon.classList.replace("close-icon", "check-icon");
                icon.style.display = "block";
            }
            break;

        case loadingStates.LOADING_STATES.LOADING:
            stepElement.classList.remove("success-step", "error-step");
            stepElement.classList.add("loading-step");
            if (icon) {
                icon.classList.replace("check-icon", "close-icon");
                icon.style.display = "none";
            }
            break;

        case loadingStates.LOADING_STATES.FAILED:
            stepElement.classList.remove("loading-step", "success-step");
            stepElement.classList.add("error-step");
            if (icon) {
                icon.classList.replace("check-icon", "close-icon");
                icon.style.display = "block";
            }
            break;
    }
}

/**
 * Updates the text content of the stolen check status element to reflect the number of successful checks.
 * This function dynamically updates the displayed text to inform the user about how many countries
 * have successfully returned a check regarding the stolen status of the vehicle.
 * @param {number} successCount - The number of countries that have successfully completed the stolen vehicle check.
 */
function updateStolenCheckText(successCount) {
    const stolenCheckTextElement = document.querySelector('.stolen-check-p');
    if (stolenCheckTextElement) {
        stolenCheckTextElement.textContent = trans(":number countries available", {number: successCount});
    }
}
function updateWreckageCheckText(successCount) {
    const wreckageCheckTextElement = document.querySelector('.vehicle-wreckers-p');
    if (wreckageCheckTextElement) {
        wreckageCheckTextElement.textContent = trans(":number countries available", {number: successCount});
    }
}
/**
 * Updates the UI elements to reflect the completion or failure of the VIN lookup process.
 * This includes setting the header text and configuring the button based on the lookup result.
 * @param {boolean} isSuccess - Indicates whether the VIN lookup was successful.
 */
function finalizeVINLookupDecode(isSuccess) {
    const headerText = document.querySelector('.jsDecodeHeader');
    if (headerText) {
        if (isSuccess) {
            headerText.innerHTML = 'VIN Lookup <strong>completed</strong>';
        } else {
            headerText.innerHTML = 'VIN Lookup <strong>Failed</strong>';
        }
    }
    
    // Update modal label when all items fail
    if (!isSuccess) {
        const modalLabel = document.getElementById('decodeModalLabel');
        if (modalLabel) {
            modalLabel.textContent = trans("We couldn't decode your vehicle this time. Please try again later.");
        }
    }
    const button = document.getElementById("decode-btn");
    if (button) {
        if (isAlreadyPurchased && isSuccess) {
            // Re-access of an already-owned report: skip purchase/credit gates and just reload.
            // The reloaded request hits VinController::decode() which now finds the cached
            // data via isDecodingCompleted() and renders the full report directly.
            button.textContent = trans("View Report");
            button.classList.remove("progress-loading", "btn-primary", "btn-danger");
            button.classList.add("btn-success");
            button.onclick = () => window.location.reload();
        } else if (!hasUser) {
            button.textContent = trans("Login to view report");
            button.classList.remove("progress-loading", "btn-primary", "btn-success");
            button.classList.add("btn-primary");
            button.onclick = () => window.location.href = route("signIn") + "?redirect_to=" + encodeURIComponent(window.location.href);
        } else if (!hasCredits) {
            button.textContent = trans("Insufficient credits");
            button.classList.remove("progress-loading", "btn-primary", "btn-success");
            button.classList.add("btn-danger");
            button.onclick = () => window.location.href = route("pricing.individualReports");

            showToast(trans("You've used all available reports. Buy credits to view this report."), Toast.WARNING, 10000);
        } else if (isSuccess) {
            button.textContent = trans("View Report");
            button.classList.remove("progress-loading", "btn-primary", "btn-danger");
            button.classList.add("btn-success");
            button.onclick = () => successBtnAction();
        } else {
            button.textContent = trans("Return to Homepage");
            button.classList.remove("progress-loading", "btn-primary", "btn-success");
            button.classList.add("btn-danger");
            button.onclick = () => window.location.href = route("home");
        }
        button.disabled = false;
    }
}

async function successBtnAction() {
    try {
        await requestVehicleReport(vin);
        window.location.reload();
    } catch (error) {
        const response = error?.response;

        if (response?.status === 401) {
            await loadAndShowModal('login', {
                onLoad: () => {
                    initializeLoginModal();
                    bindModalTriggerLinks();
                }
            });
        }
    }
}

/**
 * Adds event listeners to all elements with the 'expand-action' class. When clicked, each button
 * toggles the visibility of its associated content and updates its text to reflect the current state.
 */
document.querySelectorAll('.expand-action').forEach(expandButton => {
    expandButton.addEventListener('click', function () {
        let content = expandButton.nextElementSibling;
        content.classList.toggle('d-none');
        let expandText = expandButton.querySelector('span');
        if (content.classList.contains('d-none')) {
            expandText.textContent = 'Expand ' + expandText.textContent.split(' ').slice(1).join(' ');
        } else {
            expandText.textContent = 'Collapse ' + expandText.textContent.split(' ').slice(1).join(' ');
        }
    });
});
const downloadPDFReportButton = document.querySelectorAll('#js-downloadPdf');
if (downloadPDFReportButton.length > 0) {
    downloadPDFReportButton.forEach(button => {
        button.addEventListener('click', function () {
            // TO DO: Implement me
        });
    });
}
/**
 * Adds an event listener to redirect to the homepage.
 */
const goToHomepageLink = document.getElementById('goToHomepage');
if (goToHomepageLink) {
    goToHomepageLink.addEventListener('click', function () {
        window.location.href = route("home");
    });
}

/**
 * Clears all active timeouts for VIN decoding steps and resets the internal map.
 * This is typically called when decoding completes successfully or is cancelled.
 */
function clearAllStepTimeouts() {
    for (const timeoutId of STEP_TIMEOUTS.values()) {
        clearTimeout(timeoutId);
    }
    STEP_TIMEOUTS.clear();
}

/**
 * Determines the appropriate status for a step when timing out, based on the latest data.
 * For multi-item steps (police, wreckage), returns SUCCESS if at least one sub-item succeeded,
 * otherwise returns FAILED. The timeout means we're done waiting, so we resolve to a final state.
 * @param {string} stepId - The ID of the step to evaluate
 * @returns {string} The appropriate loading state (SUCCESS or FAILED)
 */
function determineTimeoutStatus(stepId) {
    const { FAILED, SUCCESS } = loadingStates.LOADING_STATES;
    const group = STEP_CONFIG[stepId];
    
    if (!group) {
        console.warn(`No configuration found for step: ${stepId}`);
        return FAILED;
    }
    
    const groupData = latestData[group];
    if (!groupData) {
        return FAILED;
    }
    
    const statuses = Object.values(groupData);
    
    if (!Array.isArray(statuses) || statuses.length === 0) {
        return FAILED;
    }
    
    // If there's only one item, check if it succeeded
    // If there are multiple items, check if at least one succeeded
    return statuses.includes(SUCCESS) ? SUCCESS : FAILED;
}

/**
 * Initializes timeout watchers for each VIN decoding step.
 * If a step does not complete within the defined TIMEOUT_LIMIT_MS, it will be marked as failed.
 * Once all steps are either successful or failed, the stream is closed and the process is finalized.
 */
function initStepTimeouts() {
    VIN_DECODE_STEPS.forEach(stepId => {
        clearTimeout(STEP_TIMEOUTS.get(stepId));

        const timeoutId = setTimeout(() => {
            const el = document.getElementById(stepId);

            if (el && el.classList.contains('loading-step')) {
                console.warn(`Step '${stepId}' timed out after ${TIMEOUT_LIMIT_MS}ms.`);
                
                // Determine the appropriate status based on latest data
                const status = determineTimeoutStatus(stepId);
                updateStepStatus(stepId, status);
            }

            STEP_TIMEOUTS.delete(stepId);

            if (STEP_TIMEOUTS.size === 0 && stream) {
                stream.close();

                const successExists = VIN_DECODE_STEPS.some(id => {
                    const el = document.getElementById(id);
                    return el && el.classList.contains('success-step');
                });

                finalizeVINLookupDecode(successExists);
            }
        }, TIMEOUT_LIMIT_MS);

        STEP_TIMEOUTS.set(stepId, timeoutId);
    });
}

